
import * as React from 'react';
import { EventGroup } from '@uifabric/utilities/lib/EventGroup';
import { WithAction, IActionStateProps } from '../withAction/WithAction';
import { css } from '@uifabric/utilities/lib/css';
import * as ActionDropZoneStyles from './ActionDropZone.scss';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { IComponentDecorator, IDecoratedComponentType, IDecoratedProps } from '../../utilities/components/Decorators';
import { createTelemetryProvider } from '../telemetry/TelemetryProvider';
import { IEventContext } from '@ms/telemetry-manager/lib/IEvent';

interface IDropZoneState {
  dragCounter: number;
  handled: boolean;
  hasDropped: boolean;
  hasDropFinished: boolean;
}

const DROP_ZONE_TELEMETRY_CONTEXT: IEventContext = {
  usageType: 'Drop'
};

const DropZoneTelemetryProvider = createTelemetryProvider({
  withContext: DROP_ZONE_TELEMETRY_CONTEXT
});

/**
 * Represents the props passed to the render prop callback for the drop zone.
 * @public
 */
export interface IActionDropZoneStateProps {
  /**
   * Whether the drop zone is eligible for the current drag content.
   */
  isDroppable: boolean;
  /**
   * Whether the drop zone currently has a valid payload being dragged within its bounds.
   */
  isDropping: boolean;
  /**
   * Whether an item has been dropped in the drop zone
   */
  hasDropped: boolean;
  /**
   * Whether the drop action for the item has completed
   */
  hasDropFinished: boolean;
  /**
   * A set of props to which to attach to the target DOM element.
   */
  targetProps?: Partial<React.HTMLAttributes<HTMLElement>>;
  /**
   * A ref to attach to a wrapped DOM element.
   */
  ref?: React.RefObject<HTMLElement>;
}

/**
 * The default props passed to the render prop by `ActionDropZone`.
 * @beta
 */
export const DEFAULT_ACTION_DROP_ZONE_STATE_PROPS: IActionDropZoneStateProps = {
  isDroppable: false,
  isDropping: false,
  hasDropped: false,
  hasDropFinished: false
};

/**
 * @public
 */
export interface IActionDropZoneProps extends IRenderProps<IActionDropZoneStateProps> {
  /**
   * Sets the layout mode for the Drop Zone, to either wrap content tightly using normal static
   * positioning ('static') or to fill the parent bounds using absolute positioning ('absolute').
   */
  layoutMode?: 'static' | 'absolute';
  /**
   * Whether or not the drag actions should be enabled for the wrapped content.
   * Default is `false`.
   */
  enableDrag?: boolean;
}

/**
 * @public
 */
export type IDecorateAsActionDropZoneProps<TProps, TOwnProps> = IDecoratedProps<TProps, {}, TOwnProps & IActionDropZoneProps>;

/**
 * Component decorator which dispatches a drop event to a target action and executes it if available.
 * @param canHandleEvent - A function which determines whether or not to intercept the event.
 * @param mapEventToProps - A function to map the event to props on the action to be executed.
 * @public
 */
export function asActionDropZone<TOwnProps = {}>(
  canHandleEvent?: (event: React.DragEvent<HTMLElement>, props: TOwnProps) => boolean
): IComponentDecorator<{}, TOwnProps & IActionDropZoneProps> {
  return <P extends {}>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, {}, TOwnProps & IActionDropZoneProps> => {
    const Action = component;

    type IProps = IDecoratedProps<P, {}, TOwnProps & IActionDropZoneProps>;

    type IDropZoneProps = IActionDropZoneProps & Pick<IActionStateProps, 'isAvailable' | 'execute'> & {
      onHasStartedDrag: () => void;
    };

    /**
     * Component which executes a wrapped action for each occurrence of a drop operation.
     */
    class DropZone extends React.PureComponent<IDropZoneProps, IDropZoneState> {
      private _events: EventGroup;

      constructor(props: IDropZoneProps) {
        super(props);

        this._events = new EventGroup(this);

        this.state = {
          dragCounter: 0,
          handled: false,
          hasDropped: false,
          hasDropFinished: false
        };
      }

      public render(): JSX.Element {
        const {
          children
        } = this.props as IActionDropZoneProps;

        const isDropping = this.state.dragCounter > 0 && this.state.handled;

        return (
          <div
            onDrop={ this._onDrop }
            onDragOver={ this._onDragOver }
            onDragLeave={ this._onDragLeave }
            onDragEnter={ this._onDragEnter }
            className={ css(ActionDropZoneStyles.root, {
              [ActionDropZoneStyles.dragOver]: isDropping,
              [ActionDropZoneStyles.snap]: this.props.layoutMode === 'absolute'
            }) }
          >
            {
              renderChildrenWithProps<IActionDropZoneStateProps>(children, {
                isDroppable: false,
                isDropping: isDropping,
                hasDropped: this.state.hasDropped,
                hasDropFinished: this.state.hasDropFinished
              })
            }
          </div>
        );
      }

      public componentDidMount(): void {
        this._events.on(document, 'drop', (event: DragEvent) => {
          // Need to intercept global drop events in order to prevent the browser
          // from handling it in an unwanted way.
          event.preventDefault();
        });

        this._events.on(document, 'dragenter', () => {
          this.props.onHasStartedDrag();
        }, true);
      }

      public componentWillUnmount(): void {
        this._events.dispose();
      }

      private _onDragOver = (event: React.DragEvent<HTMLElement>): void => {
        // Need to intercept drag-over in order for the browser to be willing to release the payload.
        event.preventDefault();
      }

      private _onDrop = (event: React.DragEvent<HTMLElement>): void => {
        event.preventDefault();

        if (this.props.isAvailable &&
          (!canHandleEvent || canHandleEvent(event, this.props as {} as Readonly<TOwnProps>))) {
          // The event will be queued and handed off to an executing action instance
          // once available. Since React reuses actions, this event needs to be
          // force-persisted.
          event.persist();
          event.stopPropagation();

          (async () => {
            try {
              this.props.execute({
                event: event
              });
            } catch (e) {
              // Ignore errors.
            } finally {
              this.setState({
                hasDropFinished: true
              });
            }
          })();

          this.setState({
            dragCounter: 0,
            handled: false,
            hasDropped: true
          });
        }
      }

      private _onDragEnter = (event: React.DragEvent<HTMLElement>): void => {
        event.preventDefault();

        const startHandlingEvent = this.state.dragCounter === 0 &&
          this.props.isAvailable &&
          (!canHandleEvent || canHandleEvent(event, this.props as {} as Readonly<TOwnProps>));

        if (startHandlingEvent || this.state.handled) {
          event.stopPropagation();
        }

        this.setState((previousState: IDropZoneState): IDropZoneState => {
          return {
            ...previousState,
            handled: startHandlingEvent ? true : previousState.handled,
            dragCounter: previousState.dragCounter + 1,
            hasDropped: false,
            hasDropFinished: false
          };
        });
      }

      private _onDragLeave = (event: React.DragEvent<HTMLElement>): void => {
        if (this.state.handled) {
          event.stopPropagation();
        }

        this.setState((previousState: IDropZoneState): IDropZoneState => {
          const dragCounter = previousState.dragCounter - 1;

          return {
            ...previousState,
            handled: dragCounter === 0 ? false : previousState.handled,
            dragCounter: dragCounter
          };
        });
      }
    }

    interface IActionDropZoneState {
      hasStartedDrag?: boolean;
    }

    class ActionDropZone extends React.PureComponent<IProps, IActionDropZoneState> {
      constructor(props: IProps) {
        super(props);

        this.state = {};
      }

      public render(): JSX.Element {
        const {
          children,
          ...dropZoneProps
        } = this.props as IActionDropZoneProps;

        const {
          layoutMode,
          enableDrag,
          ...actionProps
        } = dropZoneProps;

        return (
          <WithAction
            action={ this.state.hasStartedDrag ? (
              <DropZoneTelemetryProvider>
                {
                  // Avoid creating the action until there is a signal to do so,
                  // since mounting an action is fairly expensive.
                }
                <Action
                  { ...actionProps as P }
                />
              </DropZoneTelemetryProvider>
            ) : null }
            actionKey='actionDropZone'
          >
            { ({ isAvailable, execute }: IActionStateProps) => {
              return (
                <DropZone
                  { ...dropZoneProps }
                  isAvailable={ isAvailable }
                  execute={ execute }
                  onHasStartedDrag={ this._onHasStartedDrag }
                >
                  { children }
                </DropZone>
              );
            } }
          </WithAction>
        );
      }

      private _onHasStartedDrag = (): void => {
        this.setState({
          hasStartedDrag: true
        });
      }
    }

    return ActionDropZone;
  };
}
